import React, { useEffect, useState, useRef } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Avatar, Button } from "@mui/material";
import { CgMenuBoxed } from "react-icons/cg";
import { AiOutlineSetting, AiOutlineFileText } from "react-icons/ai";
import { FaUser, FaUserShield, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { FiCreditCard } from "react-icons/fi";
import permissionConfig from "../dashboard/permissionConfig";
import {
  clearOnboardingState,
  isOnboardingComplete,
  getAuthRole,
  logout,
} from "../../utils/authHelpers";
import axiosInstance from "../../utils/axiosConfig";
import rentexcelLogo from "../images/rentexcel-logo.png";
import "./DashboardLayout.css";

const DashboardLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const scrollableContainerRef = useRef(null);

  const [openDropdown, setOpenDropdown] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [title, setTitle] = useState("Dashboard");

  useEffect(() => {
    if (scrollableContainerRef.current) {
      scrollableContainerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    const path = location.pathname;
    const segments = path.split("/").filter(Boolean);
    const customMappings = {
      clientonboarding: "Client Onboarding",
      onboarding: "Onboarding",
      planname: "Plan Name",
      "my-plan": "My Plan",
      agreement: "Agreement",
    };
    const formattedPath = segments.map((segment) => {
      if (customMappings[segment]) return customMappings[segment];
      return segment.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
    });
    setTitle(formattedPath.join(" / "));
  }, [location.pathname]);

  useEffect(() => {
    const role = getAuthRole();
    const config = permissionConfig.find((item) => item.role === role);

    const initialMenuItems = [
      {
        name: "Onboarding",
        icon: <AiOutlineFileText className="nav-icon" />,
        path: "/onboarding",
      },
      {
        name: "Subscription",
        icon: <AiOutlineFileText className="nav-icon" />, 
        path: "/subscription",
        subMenu: [
          { name: "Manage Subscriptions",    icon: <AiOutlineFileText className="nav-icon" />, path: "/subscription/manage-subscriptions" },
          { name: "Plan Resource Limits",    icon: <AiOutlineFileText className="nav-icon" />, path: "/subscription/plan-limits" },
          { name: "Add-ons",                 icon: <AiOutlineFileText className="nav-icon" />, path: "/subscription/addons" },
          { name: "Add-on Resource Extras",  icon: <AiOutlineFileText className="nav-icon" />, path: "/subscription/addon-resource-extras" },
          { name: "Tenant Add-ons",          icon: <AiOutlineFileText className="nav-icon" />, path: "/subscription/tenant-addons" },
          { name: "Tenant Usage Inspector",  icon: <AiOutlineFileText className="nav-icon" />, path: "/subscription/tenant-usage" },
        ],
      },
      {
        name: "Settings",
        icon: <AiOutlineSetting className="nav-icon" />,
        path: "/settings",
        subMenu: [
          { name: "Modules", icon: <AiOutlineFileText className="nav-icon" />, path: "/settings/modules" },
          { name: "Submodules", icon: <AiOutlineFileText className="nav-icon" />, path: "/settings/submodules" },
          { name: "Plan Name", icon: <AiOutlineFileText className="nav-icon" />, path: "/settings/planname" },
          { name: "Plans", icon: <AiOutlineFileText className="nav-icon" />, path: "/settings/plans" },
          { name: "Features", icon: <AiOutlineFileText className="nav-icon" />, path: "/settings/features" },
          { name: "SiteData", icon: <AiOutlineFileText className="nav-icon" />, path: "/settings/sitedata" },
          { name: "My Plan", icon: <FiCreditCard className="nav-icon" />, path: "/settings/my-plan" },
          { name: "Agreement", icon: <AiOutlineFileText className="nav-icon" />, path: "/settings/agreement" },
          { name: "Roles", icon: <FaUserShield className="nav-icon" />, path: "/settings/roles" },
          { name: "Users", icon: <FaUser className="nav-icon" />, path: "/settings/users" },
          { name: "Industries", icon: <AiOutlineFileText className="nav-icon" />, path: "/settings/industries" },
          { name: "Template Library", icon: <AiOutlineFileText className="nav-icon" />, path: "/settings/template-library" },
        ],
      },
      {
        name: "Work List",
        icon: <CgMenuBoxed className="nav-icon" />,
        path: "/worklist",
        subMenu: [
          { name: "Enquires", icon: <AiOutlineFileText className="nav-icon" />, path: "/worklist/enquires" },
          { name: "Client Onboarding", icon: <AiOutlineFileText className="nav-icon" />, path: "/worklist/clientonboarding" },
        ],
      },
      {
        name: "Marketing",
        icon: <CgMenuBoxed className="nav-icon" />,
        path: "/marketing",
        subMenu: [
          { name: "Blog Categories", icon: <AiOutlineFileText className="nav-icon" />, path: "/marketing/blog-categories" },
          { name: "Blog Posts", icon: <AiOutlineFileText className="nav-icon" />, path: "/marketing/blog-posts" },
        ],
      },
    ];

    let updatedMenu = [...initialMenuItems];

    if (config) {
      let allowedNames = [...config.show];

      // For Client, only show Settings after onboarding is completed or plan has been purchased
      const hasPlanOrCompleted =
        isOnboardingComplete() ||
        localStorage.getItem("payment_completed") === "true" ||
        localStorage.getItem("saas_invoice_id") ||
        localStorage.getItem("checkout_session_id") ||
        location.pathname.startsWith("/settings");

      if (role === "Client" && !hasPlanOrCompleted) {
        allowedNames = allowedNames.filter((name) => name !== "Settings");
      }

      // First, filter top-level items using allowedNames
      updatedMenu = initialMenuItems.filter((item) =>
        allowedNames.includes(item.name)
      );

      if (role === "Admin" || role === "Client") {
        updatedMenu = updatedMenu.map((item) => {
          if (item.name === "Settings" && item.subMenu) {
            return {
              ...item,
              subMenu: item.subMenu.filter(
                (sub) => sub.name === "SiteData" || sub.name === "My Plan" || sub.name === "Agreement"
              ),
            };
          }
          if (item.name === "Subscription" && item.subMenu) {
            return {
              ...item,
              subMenu: item.subMenu.filter((sub) => sub.name === "Manage Subscriptions"),
            };
          }
          return item;
        });
      } else if (role === "SuperAdmin") {
        updatedMenu = updatedMenu.map((item) => {
          if (item.name === "Settings" && item.subMenu) {
            return {
              ...item,
              subMenu: item.subMenu.filter(
                (sub) => sub.name !== "SiteData" && sub.name !== "My Plan" && sub.name !== "Agreement"
              ),
            };
          }
          return item;
        });
      }
    }

    setMenuItems(updatedMenu);

    // Open correct dropdown on load
    updatedMenu.forEach((item, index) => {
      if (location.pathname.startsWith(item.path)) {
        setOpenDropdown(index);
      }
    });

  }, [location.pathname]);

  useEffect(() => {
    const role = getAuthRole();
    if (
      role === "Client" &&
      !isOnboardingComplete() &&
      !location.pathname.startsWith("/onboarding") &&
      !location.pathname.startsWith("/payment") &&
      !location.pathname.startsWith("/settings") &&
      !location.pathname.startsWith("/my-plan")
    ) {
      navigate("/onboarding", { replace: true });
    }
  }, [location.pathname, navigate]);

  const handleLogout = () => {
    logout(navigate);
  };

  const isRentexcel = axiosInstance.defaults.baseURL.includes("rentexcel");

  return (
    <div className="dashboard-layout">
      <aside className="sidebar-main-container">
        <div className="sidebar-brand">
          {isRentexcel ? (
            <img
              src={rentexcelLogo}
              alt="logo"
              className="sidebar-logo"
              style={{ maxWidth: '150px', cursor: 'pointer', background: 'white', borderRadius: '5px', padding: '5px' }}
              onClick={() => window.location.href = "/"}
            />
          ) : (
            <span
              style={{ cursor: 'pointer', color: 'white', fontWeight: 'bold', fontSize: '22px' }}
              onClick={() => window.location.href = "/"}
            >
              Taproot Admin
            </span>
          )}
        </div>
        <nav className="sidebar-nav">
          {menuItems.map((item, index) => (
            <React.Fragment key={index}>
              <div
                className={`d-flex justify-content-between align-items-center nav-link ${location.pathname.startsWith(item.path) && !item.subMenu ? "active" : ""}`}
                onClick={() => {
                  if (item.subMenu) {
                    setOpenDropdown(openDropdown === index ? null : index);
                  } else {
                    navigate(item.path);
                  }
                }}
                style={{ cursor: "pointer", marginTop: '10px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {item.icon}
                  {item.name}
                </div>
                {item.subMenu && (
                  <div>{openDropdown === index ? <FaChevronUp /> : <FaChevronDown />}</div>
                )}
              </div>
              {item.subMenu && openDropdown === index && (
                <div style={{ paddingLeft: '15px' }}>
                  {item.subMenu.map((sub, subIdx) => (
                    <NavLink to={sub.path} className="nav-link" key={subIdx}>
                      {sub.icon} {sub.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}
        </nav>
      </aside>

      <div className="dashboard-main-area">
        <header className="dashboard-top-header">
          <div className="header-left">
            <p style={{ fontSize: "18px", fontWeight: "bold", margin: 0, color: "#1d273b" }}>{title}</p>
          </div>
          <div style={{ marginLeft: "auto", display: 'flex', alignItems: 'center', gap: '15px' }}>
            {/* <Avatar
              alt="user"
              src={require("../images/avatar.png")}
            /> */}
            <Button
              variant="contained"
              onClick={handleLogout}
              sx={{
                backgroundColor: "#f59e0b",
                "&:hover": {
                  backgroundColor: "#d97706",
                },
              }}
            >
              Logout
            </Button>
          </div>
        </header>
        <div className="dashboard-content" id="scroll" ref={scrollableContainerRef}>
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
